/**
 * A machine's tag roles (equipment intelligence E1, Task 2; backend platform-core/internal/twin/equipment_roles.go,
 * contract api/openapi.yaml EquipmentRole). Each port a tag is bound to on a machine takes one of five roles: key_value
 * (the few values that define what the machine makes, at most eight), control, health, config or hidden. The platform
 * suggests a role; only a role the plant accepted is the machine's.
 *
 *   GET /v1/twin/equipment/{entity_id}/roles   {roles}: every bound port with its suggestion, its accepted role, its
 *                                              state and the kind of yardstick it is judged by. Any member reads.
 *   PUT /v1/twin/equipment/{entity_id}/roles   {roles: [{port, role}]} -> {roles, job?}. model.write. An empty role
 *                                              withdraws the accepted one. `job` (the analysis refresh the write
 *                                              queued) is absent when nothing changed.
 *     400 too_many_key_values                  a machine accepts at most eight key values
 *     400 unknown_port                         a port that is not bound to the machine
 *
 * The web shows the server's yardstick kind and never derives the ladder itself. A tolerance around a target is a
 * process window on "<port> from target" and is saved through lib/api/limits.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { errorCodeOf } from "@/lib/api/error";
import { useBriefLive } from "@/lib/api/brief";

/** The most key values a machine accepts (the backend's equipmentKeyValuesMax). */
export const KEY_VALUES_MAX = 8;

/** Said when a choice or an accept would pass the limit; the platform refuses it with the same words. */
export const KEY_VALUES_MAX_TEXT = `A machine keeps at most ${KEY_VALUES_MAX} key values. Move one to another role first.`;

/** Said when Accept suggestions would pass the limit: it takes all or none, so it points at what does work. */
export const KEY_VALUES_MAX_ACCEPT_TEXT = `A machine keeps at most ${KEY_VALUES_MAX} key values. Accept them one at a time, or move one to another role first.`;

/** The five roles, in the order a machine's page lists them. */
export const EQUIPMENT_ROLES = ["key_value", "control", "health", "config", "hidden"] as const;
export type EquipmentRoleName = (typeof EQUIPMENT_ROLES)[number];

/** A role as the wire carries it: one of the five, or empty (none suggested, or none accepted). */
export type EquipmentRoleValue = EquipmentRoleName | "";

/** Where a suggestion came from. */
export type EquipmentSuggestedFrom = "class" | "pair" | "data_quality" | "none";

/**
 * Whether the plant accepted a role, only a suggestion stands, or two tags feed the port and cannot be read as one
 * series (two_feeds). A port fed by several sources that can be is accepted or suggested like any other.
 */
export type EquipmentRoleState = "accepted" | "suggested" | "two_feeds";

/** The kind of yardstick a key value or a control is judged by; empty for any other role. */
export type EquipmentYardstick = "record_target" | "plant_window" | "baseline" | "";

/**
 * One source of a port fed by several (one series per port, S2.2; EquipmentRoleSource in api/openapi.yaml): a tag bound
 * primary on the port, what it is, its rank, whether the port may read it and whether it reads it now.
 */
export interface EquipmentRoleSource {
  tag_id: string;
  tag_name: string;
  /** The tag's unit as recorded; its values are read in the port's unit. */
  unit: string;
  thing_id: string;
  thing_slug: string;
  data_source_id: string | null;
  /** Empty for a source the port never reads. */
  source_kind: "live" | "history" | "report" | "manual" | "";
  /** 1 ranks first; 0 for a source the port never reads. */
  rank: number;
  read: boolean;
  of_record: boolean;
  /** Where its rank comes from, or why the port never reads it, in words. */
  why: string;
}

export interface EquipmentRole {
  port: string;
  /** The tag feeding the port (for a port fed by several sources, the one it reads now); null for a two_feeds port. */
  tag_id: string | null;
  /** The tag's own name; empty for a two_feeds port. */
  tag_name: string;
  /** The port's unit (a port fed by several sources: its series' unit); empty when a two_feeds port's feeds disagree. */
  unit: string;
  suggested: EquipmentRoleValue;
  suggested_from: EquipmentSuggestedFrom;
  /** The role the plant accepted; empty while only suggested. */
  role: EquipmentRoleValue;
  state: EquipmentRoleState;
  yardstick: EquipmentYardstick;
  /**
   * B6: the yardstick named by `yardstick`, whole (the server's Yardstick), so a board's widget draws it and never
   * derives it: a plant window's ends in the port's unit, a record target's setpoint port and its tolerance (deviations,
   * in the unit's difference), a baseline with no ends. Null for any other role; absent from an older server.
   */
  band?: EquipmentYardstickBand | null;
  /** A port fed by several sources lists them; absent for every other port, and from an older server. */
  sources?: EquipmentRoleSource[];
  /** With state two_feeds: why its feeds cannot be read as one series, in words. Absent otherwise. */
  two_feeds_reason?: string;
}

/** A yardstick whole, as the roles route carries it (EquipmentYardstick in api/openapi.yaml). */
export interface EquipmentYardstickBand {
  kind: "record_target" | "plant_window" | "baseline" | "none";
  target_port: string;
  lo: number | null;
  hi: number | null;
  unit: string;
  label: string;
  source: string;
}

/** One entry of a PUT: the port and the role to accept, or empty to withdraw the accepted one. */
export interface EquipmentRolePut {
  port: string;
  role: EquipmentRoleValue;
}

export interface EquipmentRoles {
  roles: EquipmentRole[];
}

/** The analysis refresh a write that changed a role queued (the fields the card does not read are left out). */
export interface EquipmentRolesJob {
  id: string;
  status: string;
  reasons: string[];
}

export interface EquipmentRolesWritten extends EquipmentRoles {
  /** Absent when the write changed nothing. */
  job?: EquipmentRolesJob;
}

const enc = encodeURIComponent;

export const equipmentRolesPath = (entityId: string) => `/v1/twin/equipment/${enc(entityId)}/roles`;

/** The roles as the page reads them: a Go nil list as empty. */
export function equipmentRolesOf(raw: Partial<EquipmentRoles> | null | undefined): EquipmentRoles {
  return { roles: raw?.roles ?? [] };
}

export async function listEquipmentRoles(entityId: string): Promise<EquipmentRoles> {
  return equipmentRolesOf(await api.get<EquipmentRoles>(equipmentRolesPath(entityId)));
}

export async function putEquipmentRoles(entityId: string, roles: EquipmentRolePut[]): Promise<EquipmentRolesWritten> {
  const raw = await api.put<EquipmentRolesWritten>(equipmentRolesPath(entityId), { roles });
  return { ...equipmentRolesOf(raw), job: raw?.job };
}

export const EQUIPMENT_ROLES_KEY = ["equipment-roles"] as const;
export const equipmentRolesKey = (entityId: string) => [...EQUIPMENT_ROLES_KEY, entityId] as const;

/** One machine's roles, on the live platform only. */
export function useEquipmentRoles(entityId: string | null | undefined) {
  const live = useBriefLive();
  return useQuery({
    queryKey: equipmentRolesKey(entityId ?? ""),
    queryFn: () => listEquipmentRoles(entityId as string),
    enabled: live && !!entityId,
    staleTime: 30_000,
  });
}

/**
 * Accepts, changes or withdraws roles. The answer carries every role after the write, so the cached read is replaced
 * with it (its yardsticks included) rather than patched here.
 */
export function usePutEquipmentRoles(entityId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation(putRolesMutation(qc, entityId));
}

/**
 * The write's options as a plain object (as lagAcceptMutation builds the lag accept's), so a test drives them with a
 * stub client: the answer replaces the cached read, and a refusal reads the roles again.
 */
export function putRolesMutation(
  qc: Pick<QueryClient, "setQueryData" | "invalidateQueries">,
  entityId: string | null | undefined,
) {
  return {
    mutationFn: (roles: EquipmentRolePut[]) => putEquipmentRoles(entityId as string, roles),
    onSuccess: (res: EquipmentRolesWritten) => {
      qc.setQueryData(equipmentRolesKey(entityId ?? ""), equipmentRolesOf(res));
    },
    onError: () => {
      // A refused write may mean the page is out of date (a port unbound since it was read): read it again.
      void qc.invalidateQueries({ queryKey: equipmentRolesKey(entityId ?? "") });
    },
  };
}

/** What the page says when the roles are not saved, in plain words from the code. */
export function equipmentRolesSaveErrorText(err: unknown): string {
  switch (errorCodeOf(err)) {
    case "too_many_key_values":
      return KEY_VALUES_MAX_TEXT;
    case "unknown_port":
      return "A port named here is no longer bound to this machine. The list has been read again.";
    case "insufficient_permission":
      return "Changing a machine's roles needs permission to edit the model.";
    case "entity_not_found":
      return "This machine could not be found in the organisation.";
  }
  return "The roles were not saved. Check the connection and try again.";
}

/** What the page says when the roles cannot be read. */
export function equipmentRolesReadErrorText(err: unknown): string {
  return errorCodeOf(err) === "entity_not_found"
    ? "No equipment with this id is readable in this organisation."
    : "The roles could not be read. Check the connection and try again.";
}
